import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './NewsPage.css';

interface NewsItem {
  id: number;
  name: string;
  date: string;
  category: string;
  title: {
    en: string;
    zh: string;
    ja?: string;
  };
  summary: {
    en: string;
    zh: string;
    ja?: string;
  };
  content: {
    en: string;
    zh: string;
    ja?: string;
  };
  image: string;
}

// Simple markdown parser for news content
const parseMarkdown = (text: string): string => {
  let html = text;
  
  // Links: [text](url) - check if internal (synorlab.com) or external
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, linkText, url) => {
    // Check if it's an internal link (synorlab.com or relative path)
    const isInternal = url.startsWith('/') || 
                       url.startsWith('synorlab.com') || 
                       url.startsWith('https://synorlab.com') ||
                       url.startsWith('http://synorlab.com');
    
    if (isInternal) {
      // Convert synorlab.com URLs to relative paths for internal navigation
      let href = url;
      if (url.startsWith('https://synorlab.com')) {
        href = url.replace('https://synorlab.com', '');
      } else if (url.startsWith('http://synorlab.com')) {
        href = url.replace('http://synorlab.com', '');
      } else if (url.startsWith('synorlab.com')) {
        href = url.replace('synorlab.com', '');
      }
      // Ensure it starts with /
      if (!href.startsWith('/')) {
        href = '/' + href;
      }
      return `<a href="${href}" class="internal-link">${linkText}</a>`;
    } else {
      // External link - open in new tab
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
    }
  });

  // Auto-link product name keywords to product page.
  // This runs after markdown links so explicit links remain unchanged.
  html = html.replace(
    /(個人收藏|Personal Vault)/g,
    '<a href="/personal-vault" class="internal-link">$1</a>'
  );
  
  // Bold: **text** or __text__
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');
  
  // Italic: *text* or _text_
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  html = html.replace(/_([^_]+)_/g, '<em>$1</em>');
  
  return html;
};

// Block types for parsed content
type BlockType = 'paragraph' | 'list' | 'heading' | 'divider';

interface ContentBlock {
  type: BlockType;
  items: string[];
  level?: number; // For headings (1-6)
}

// Parse content into structured blocks
const parseContentBlocks = (content: string): ContentBlock[] => {
  const lines = content.split('\n');
  const blocks: ContentBlock[] = [];
  let currentList: string[] = [];
  
  for (const line of lines) {
    const trimmedLine = line.trim();
    
    // Check for horizontal rule (---, ***, ___)
    if (/^[-*_]{3,}$/.test(trimmedLine)) {
      // Push any pending list first
      if (currentList.length > 0) {
        blocks.push({ type: 'list', items: currentList });
        currentList = [];
      }
      blocks.push({ type: 'divider', items: [] });
      continue;
    }
    
    // Check for headings (## or ###)
    const headingMatch = trimmedLine.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      // Push any pending list first
      if (currentList.length > 0) {
        blocks.push({ type: 'list', items: currentList });
        currentList = [];
      }
      const level = headingMatch[1].length;
      const headingText = parseMarkdown(headingMatch[2]);
      blocks.push({ type: 'heading', items: [headingText], level });
      continue;
    }
    
    // Check if it's a list item (starts with - or *)
    if (/^[-*]\s+/.test(trimmedLine)) {
      const listContent = trimmedLine.replace(/^[-*]\s+/, '');
      currentList.push(parseMarkdown(listContent));
    } else {
      // If we were building a list, push it first
      if (currentList.length > 0) {
        blocks.push({ type: 'list', items: currentList });
        currentList = [];
      }
      
      // Add paragraph if not empty
      if (trimmedLine) {
        blocks.push({ type: 'paragraph', items: [parseMarkdown(trimmedLine)] });
      }
    }
  }
  
  // Don't forget the last list if content ends with list items
  if (currentList.length > 0) {
    blocks.push({ type: 'list', items: currentList });
  }
  
  return blocks;
};

const NewsDetailPage: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const { t, i18n } = useTranslation();
  const [news, setNews] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const response = await fetch('/news.json');
        if (!response.ok) {
          throw new Error('Failed to fetch news');
        }
        const data = await response.json();
        const foundNews = data.find((item: NewsItem) => item.name === name);
        
        if (!foundNews) {
          setError('News not found');
          setNews(null);
        } else {
          setNews(foundNews);
          setError(null);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        console.error('Error fetching news:', err);
      } finally {
        setLoading(false);
      }
    };

    if (name) {
      fetchNews();
    } else {
      setError('News name is required');
      setLoading(false);
    }
  }, [name]);

  const getLocalizedText = (obj: { en: string; zh: string; ja?: string }): string => {
    const lang = i18n.language;
    if (lang === 'zh' || lang === 'zh-TW') {
      return obj.zh;
    }
    if (lang === 'ja' && obj.ja) {
      return obj.ja;
    }
    return obj.en;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'zh' || i18n.language === 'zh-TW' ? 'zh-TW' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryLabel = (category: string): string => {
    return t(`news.categories.${category}`, category);
  };

  if (loading) {
    return (
      <div className="news-page">
        <section className="page-header">
          <div className="container">
            <h1 className="page-title">{t('news.title')}</h1>
            <p className="page-subtitle">{t('news.subtitle')}</p>
          </div>
        </section>
        <section className="news-content">
          <div className="container">
            <div className="loading-state">
              <p>{t('common.loading')}</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (error || !news) {
    return (
      <div className="news-page">
        <section className="page-header">
          <div className="container">
            <h1 className="page-title">{t('news.title')}</h1>
            <p className="page-subtitle">{t('news.subtitle')}</p>
          </div>
        </section>
        <section className="news-content">
          <div className="container">
            <div className="error-state">
              <p>{t('common.error')}: {error || 'News not found'}</p>
              <Link to="/news" className="back-button" style={{ marginTop: 'var(--spacing-4)' }}>
                ← {t('common.back')} to News
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="news-page">
      <section className="page-header">
        <div className="container">
          <h1 className="page-title">{getLocalizedText(news.title)}</h1>
          <p className="page-subtitle">{t('news.subtitle')}</p>
        </div>
      </section>

      <section className="news-content">
        <div className="container">
          <div className="news-detail">
            <Link 
              to="/news"
              className="back-button"
            >
              ← {t('common.back')}
            </Link>
            <article className="news-article">
              <div className="news-article-header">
                <span className="news-category-badge">{getCategoryLabel(news.category)}</span>
                <time className="news-date">{formatDate(news.date)}</time>
              </div>
              <h2 className="news-article-title">{getLocalizedText(news.title)}</h2>
              {news.image && (
                <div className="news-article-image">
                  <img src={news.image} alt={getLocalizedText(news.title)} />
                </div>
              )}
              <div className="news-article-content">
                <p className="news-summary">{getLocalizedText(news.summary)}</p>
                <div className="news-full-content">
                  {parseContentBlocks(getLocalizedText(news.content)).map((block, index) => {
                    if (block.type === 'divider') {
                      return <hr key={index} className="news-divider" />;
                    }
                    if (block.type === 'heading') {
                      const HeadingTag = `h${Math.min(block.level || 2, 6)}` as keyof JSX.IntrinsicElements;
                      return (
                        <HeadingTag 
                          key={index} 
                          className={`news-heading news-heading-${block.level}`}
                          dangerouslySetInnerHTML={{ __html: block.items[0] }} 
                        />
                      );
                    }
                    if (block.type === 'list') {
                      return (
                        <ul key={index} className="news-content-list">
                          {block.items.map((item, itemIndex) => (
                            <li key={itemIndex} dangerouslySetInnerHTML={{ __html: item }} />
                          ))}
                        </ul>
                      );
                    }
                    return (
                      <p key={index} dangerouslySetInnerHTML={{ __html: block.items[0] }} />
                    );
                  })}
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewsDetailPage;

